<template>
  <div class="column is-one-third-widescreen is-one-third-desktop is-half-tablet is-full-mobile">
    <router-link :to="path">
      <div class="contribution-card">
        <div class="image" :class="{'no-image': !hasImage, 'no-text': !textContent}" :style="imageBackground">
          <text-block v-if="!hasImage" :content="textContent" :projectCard="true"></text-block>
        </div>
        <div class="details" :class="{'no-image': !hasImage, 'no-text': !textContent}">
          <div class="image-and-title" :class="{'no-image': !hasImage, 'no-text': !textContent}">
            <div v-if="projectImage && !(isStory && hasImage)" class="thumbnail" :style="projectImageBackground"></div>
            <div class="title">{{project.title}}</div>
          </div>
          <div v-if="hasImage" class="description">
            <html-view :content="textContent" :hideElements="true"></html-view>
            <div class="fade"></div>
          </div>
        </div>
        <visibility-flags :project="project"></visibility-flags>
      </div>
    </router-link>
  </div>
</template>

<script>
import TextBlock from '@/components/Controls/TextBlock'
import HtmlView from '@/components/Controls/HTMLView'
import imageProxy from '@/libs/image-proxy'
import auth from '@/mixins/auth'
import VisibilityFlags from '@/components/Cards/ProjectCards/UI/visibilityFlags'

export default {
  name: 'projectcard',

  props: ['contribution'],

  components: {
    TextBlock,
    VisibilityFlags,
    HtmlView
  },

  mixins: [auth],

  computed: {
    project: function () {
      return this.contribution ? this.contribution.project : {}
    },
    comment: function () {
      return this.contribution ? this.contribution.comment || {} : {}
    },
    post: function () {
      return this.contribution ? this.contribution.post || {} : {}
    },
    postOrComment: function () {
      return this.contribution ? this.contribution.comment || this.contribution.post || {} : {}
    },
    path: function () {
      const basePath = this.project.type === 'story' ? 'story' : 'project'
      let url = `/${basePath}/${this.project.slug || this.project.id}`

      const params = []

      if (this.post && this.post.id) {
        params.push(`postID=${this.post.id}`)
      } else if (this.comment && this.comment.id) {
        params.push(`postID=${this.comment.postID}`)
        params.push(`commentID=${this.comment.id}`)
      }

      if (params.length > 0) {
        url = `${url}?${params.join('&')}`
      }

      return url
    },
    projectImageBackground: function () {
      const style = {}

      if (this.projectImage) {
        style.backgroundImage = `url('${imageProxy.project(this.projectImage)}')`
      }

      return style
    },
    projectImage: function () {
      if (this.project.image && !this.project.imageGenerated) {
        return imageProxy.thumbnail(this.project.image)
      }
    },
    imageBackground: function () {
      const style = {}

      if (this.hasImage) {
        style.backgroundImage = `url('${imageProxy.project(this.imageURL)}')`
      }

      return style
    },
    hasImage: function () {
      return this.imageURL
    },
    textContent: function () {
      if (this.isStory && this.project.description) {
        return this.project.description
      }

      if (this.postOrComment && this.postOrComment.id) {
        return this.postOrComment.body
      } else {
        return 'Whiteboard Contribution'
      }
    },
    hasAttachments: function () {
      return this.postOrComment && this.postOrComment.attachments && this.postOrComment.attachments.length > 0
    },
    showAttachment: function () {
      return !this.showTextBlock
    },
    showTextBlock: function () {
      return !this.hasAttachments || !this.imageURL
    },
    firstAttachment: function () {
      return this.hasAttachments ? this.postOrComment.attachments[0] : null
    },
    imageOffset: function () {
      if (!this.hasAttachments) {
        return null
      }

      const offsets = this.postOrComment.attachments.map((attachment, offset) => {
        if (attachment.type === 'image') {
          return offset
        }

        if ((attachment.type === 'link' || attachment.type === 'video') && (attachment.metadata.ogImage || attachment.metadata.screenshot)) {
          return offset
        }

        if (attachment.type === 'whiteboard') {
          return offset
        }
      }).filter((offsets) => offsets >= 0)

      return offsets.length > 0 ? offsets[0] : null
    },
    imageAttachment: function () {
      const offset = this.imageOffset
      return this.hasAttachments && offset >= 0 ? this.postOrComment.attachments[offset] || null : null
    },
    imageURL: function () {
      if (this.isStory && this.project.image) {
        return this.project.image
      }

      switch (this.imageAttachmentType) {
        case 'link':
        case 'video':
          if (this.imageAttachment.metadata.ogImage) {
            return this.imageAttachment.metadata.ogImage
          } else {
            return this.imageAttachment.metadata.screenshot
          }
        case 'image':
        case 'whiteboard':
          return this.imageAttachment.url
      }
    },
    imageAttachmentType: function () {
      return this.imageAttachment ? this.imageAttachment.type : null
    },
    isWhiteboard: function () {
      return this.imageAttachmentType === 'whiteboard'
    },
    isStory: function () {
      return this.project.type === 'story' && this.post.isProjectDetails
    }
  }

}
</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';

  .contribution-card {
    width: 100%;
    height: 403px;
    background: white;
    position: relative;
    cursor: pointer;
    color: #4a4a4a;
    box-shadow:0 0 11px rgba(33, 33, 33, .2);
    transition:0.4s;
    @include hover-cards-effect;

    @include mobile {
      display: flex;
      height: 200px;
      flex-wrap: wrap;
      padding: 0.5rem 0.5rem 0.5rem 0.5rem;
      max-width: 450px;
      margin:0 auto;
    }

    .image {
      height: 55%;
      padding:0;
      background-color: whitesmoke;
      background-size: cover;
      background-repeat: no-repeat;
      background-position: center;

      @include mobile {
        flex-basis: 50%;
        height: 100%;
      }

      &.no-image, &.no-text {
        @include desktop {
          height: 70%;
        }
      }
    }

    .image-link{
      height:35%;
      @include tablet-only{
      height: 50%;
      }
      @include mobile {
        height: auto;
      }
    }

    .details {
      position: relative;
      height: 45%;
      overflow: hidden;
      padding: 0.45rem 0.75rem;

      @include mobile {
        flex-basis: 50%;
        padding-left: 0.65rem;
        height: 100%;
      }

      &.no-image, &.no-text {
        @include desktop {
          height: 30%;
        }
      }

      .fade {
        position: absolute;
        height: 1.2rem;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(to right, rgba(255,255,255,0.001), rgba(255,255,255,0.8));
      }
    }

    .image-and-title {
      display: flex;
      align-items: center;

      &.no-image, &.no-text {
        height: 100%;
      }

      .title {
        font-size: 1.1rem;
        font-weight: 700;
        margin: 0.4rem 0 0.4rem 0;
        line-height: 1.5rem;
        overflow: hidden;

        @include mobile {
          margin-top: 0.5rem;
          font-size: 1rem;
          line-height: 1.3rem;
        }
      }

      .thumbnail {
        width: 50px;
        height: 50px;
        min-width: 50px;
        max-width: 50px;
        min-height: 50px;
        max-height: 50px;
        margin: 0 0.5rem 0.5rem 0;
        border-radius: 50%;
        overflow: hidden;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        box-shadow: $default-shadow;
        @include mobile {
          display: none;
        }
      }
    }

    .description {
      @include mobile {
        font-size: 0.8rem;
      }
    }

    .footer {
      margin-top: 0.2rem;
      background: none;
      padding: 0.40rem 0.75rem;
      width: 100%;

      display: flex;
      justify-content: space-between;

      @include mobile {
        flex-basis: 100%;
        margin-top: 0.15rem;
        padding: 0.10rem;
      }

      .user {
        display: flex;
        font-size:0.85rem;

        .avatar {
          height: 1.5rem;
          width: 1.5rem;
          margin-right: .4rem;

          img {
            max-width: 100%;
            max-height: 100%;
          }
        }
      }

      .metrics {
        display: flex;
        justify-content: flex-end;
        font-size:0.9rem;

        .views, .follows {
          margin-right: 0.5rem;
        }
      }
    }

    .tag {
      color: white;
      text-transform: capitalize;
      position: absolute;
      top: 1.2rem;
      padding: 0 1.2rem;
      font-weight: bold;
      font-size: 0.8rem;
      border-radius: 0;
      @include mobile {
        font-size: 0.72rem;
      }
    }
  }

  .contribution-card-link {
    height: 275px;
    cursor: default;
    @include tablet-only {
      height: 360px;
    }
    @include mobile {
      height: 200px;
    }
    .title{
      font-size: 0.9rem;
      line-height: 1rem
    }
    .description {
      font-size: 0.8rem;
    }
    .footer {
       margin-top: 0.01rem;
      @include mobile {
        margin-top: 0.2rem;
      }
    }
  }

  .user {
    cursor: pointer;
  }

  .card-container {
    width: 100%;
  }

  .liked {
    color: $primary;
  }

  .column {
    padding: 2rem 1.8rem 1rem 30px;
    @include mobile {
      padding: 1rem 0.5rem 0.5rem;
    }
  }
</style>
<style lang="scss">
  .contribution-card .description .markdown-span-override p {
    margin: 0;
  }
  .contribution-card .html-span-override a {
    color: white;
  }
</style>
